import {
  MagnifyingGlassRegularIcon,
  PlusRegularIcon,
  UploadRegularIcon,
  XMarkRegularIcon,
} from "@elixir/icons";
import {
  SERVICE_MASTER_STATUS,
  TSelectedSkuItem,
  TSkuItem,
} from "@elixir/types";
import { useDebounce } from "@elixir/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { notification } from "antd";
import React, { useState } from "react";
import {
  InputField,
  PrimaryButton,
  SecondaryButton,
  TableHeaderUi,
  Spinner,
  getSettingsBodyConfig,
} from "ui";
import useGetServicesMasterInfiniteQuery from "../../apis/axon/services/use-get-services-master-infinite-query";
import usePostBulkUploadServicesMaster from "../../apis/axon/services/use-post-bulk-upload";
import usePostCreateServicesMaster from "../../apis/axon/services/use-post-create-services-master";
import usePutUpdateServicesMaster from "../../apis/axon/services/use-put-update-services-master";
import ClinicSelector from "../queue/clinic-selector";
import BulkUploadServiceTemplateModal from "./bulk-upload-service-template-modal";
import CreateEditServicePopup from "./create-edit-service-popup";
import ManageServicesTableRowItem from "./service-table-row-item";
import { getMe } from "../../utils/bridges/methods";
import ServiceStatusDropdownSelector from "../queue/ServicesFilterDropdown";

enum ServicePopupStatus {
  CREATE,
  UPDATE,
  CLOSE,
}

export const manageServicesTableHeaderConfig: Array<{
  key: string;
  width?: string | number;
  title: string;
  align?: "left" | "right" | "center";
}> = [
  {
    key: "service_name",
    title: "Service Name",
    width: "40%",
  },
  {
    key: "price",
    title: "Price",
    width: "25%",
  },
  {
    key: "archive",
    title: "Inactive/active",
    width: "25%",
    align: "right",
  },
  {
    key: "edit",
    title: "Actions",
    width: "10%",
    align: "right",
  },
];

export const manageServicesTableBodyConfig = getSettingsBodyConfig(
  manageServicesTableHeaderConfig
);

const ManageServices = () => {
  const [serviceInput, setServiceInput] = React.useState<string>("");
  const debouncedServiceInput = useDebounce(serviceInput);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [clinicId, setClinicId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<SERVICE_MASTER_STATUS>(
    SERVICE_MASTER_STATUS.ACTIVE
  );

  React.useEffect(() => {
    if (!clinicId) {
      const { clinics } = getMe();
      if (clinics?.[0]?.id) {
        setClinicId(clinics?.[0]?.id);
      }
    }
  }, [clinicId]);

  const isSearching = debouncedServiceInput.trim().length > 0;
  const {
    data: servicesData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading: isAutosuggestLoading,
  } = useGetServicesMasterInfiniteQuery({
    query: debouncedServiceInput,
    status: selectedStatus,
    clinicIdToFetch: clinicId,
    searchInAllServiceTypes: isSearching,
  });

  const serviceRows = servicesData?.pages
    ? servicesData.pages.flatMap((d) => d.data)
    : [];

  const parentRef = React.useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? serviceRows.length + 1 : serviceRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });

  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const lastItem = items[items.length - 1];

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= serviceRows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    items,
    serviceRows.length,
  ]);

  const [servicePopupState, setServicePopupState] =
    React.useState<ServicePopupStatus>(ServicePopupStatus.CLOSE);

  const [serviceToEdit, setServiceToEdit] = React.useState<
    TSkuItem | undefined
  >(undefined);

  const { mutateAsync: postUploadBulkServices, isLoading: isUploadLoading } =
    usePostBulkUploadServicesMaster();
  const { mutateAsync: postCreateService, isLoading: isCreateLoading } =
    usePostCreateServicesMaster({ clinic_id: clinicId });
  const { mutateAsync: putUpdateService, isLoading: isUpdateLoading } =
    usePutUpdateServicesMaster({
      clinic_id: clinicId,
      status: selectedStatus,
      searchInAllServiceTypes: isSearching,
    });

  const handleCreateOrEditSubmit = async ({
    service_name,
    price,
  }: TSelectedSkuItem) => {
    if (serviceToEdit) {
      const { success: updateSuccess } = await putUpdateService({
        price: serviceToEdit.price,
        service_name: serviceToEdit.service_name,
        service_id: serviceToEdit.service_id,
        clinic_id: clinicId,
        body: {
          service_name,
          price,
          clinic_id: clinicId,
        },
      });
      if (updateSuccess) {
        notification.success({
          message: "Service updated Successfully!!",
          placement: "bottomRight",
        });
        handleCreateOrEditPopupClose();
      } else {
        notification.error({
          message: "Error occured while updating service! Please try again.",
          placement: "bottomRight",
        });
      }
    } else {
      const { success: createSuccess } = await postCreateService({
        price,
        service_name,
        clinic_id: clinicId,
      });
      if (createSuccess) {
        notification.success({
          message: "Service created Successfully!!",
          placement: "bottomRight",
        });
        handleCreateOrEditPopupClose();
      } else {
        notification.error({
          message: "Error occured while creating service! Please try again.",
          placement: "bottomRight",
        });
      }
    }
  };

  const handleCreateOrEditPopupClose = () => {
    setServicePopupState(ServicePopupStatus.CLOSE);
    if (serviceToEdit) {
      setServiceToEdit(undefined);
    }
  };

  const handleUploadClick = async (e: any) => {
    const file = e.file as File;
    const { success: uploadSuccess, error } = await postUploadBulkServices({
      servicesFile: file,
      clinic_id: clinicId,
    });
    if (uploadSuccess) {
      notification.success({
        message: "Services Uploaded Successfully!!",
        placement: "bottomRight",
      });
      handleShowUploadModal(false);
    } else {
      notification.error({
        message: error?.message || "Error occured while uploading services!",
        placement: "bottomRight",
      });
    }
  };

  const handleCreateNewService = () => {
    setServicePopupState(ServicePopupStatus.CREATE);
  };

  const handleServiceEditClick = (item: TSkuItem) => {
    setServicePopupState(ServicePopupStatus.UPDATE);
    setServiceToEdit(item);
  };

  const handleShowUploadModal = (showModal: boolean) => {
    setShowUploadModal(showModal);
  };

  return (
    <>
      <CreateEditServicePopup
        isModalOpen={servicePopupState !== ServicePopupStatus.CLOSE}
        skuItem={serviceToEdit}
        onClose={handleCreateOrEditPopupClose}
        isCreateLoading={isCreateLoading}
        isUpdateLoading={isUpdateLoading}
        handleCreateEditSumbit={handleCreateOrEditSubmit}
      />
      <div className="dr-bg-darwin-neutral-0 dr-py-12 dr-px-16 dr-space-y-16 dr-h-full dr-overflow-hidden dr-w-full">
        <div className="dr-flex dr-items-center dr-justify-between">
          <div className="HeadlineBold dr-text-darwin-neutral-1000">
            Services
          </div>
          <div className="dr-flex dr-items-center dr-space-x-12">
            <ServiceStatusDropdownSelector
              selectedStatus={selectedStatus}
              onStatusChange={(status) => {
                console.log("status change form elixr", status);

                setSelectedStatus(status);
                if (serviceInput.trim().length > 0) setServiceInput("");
              }}
              showAllStatusOption={true}
            />

            <ClinicSelector
              selectedClinicId={clinicId}
              onClinicChange={(id) => {
                setClinicId(id);
              }}
            />
            <InputField
              value={serviceInput}
              placeholder="Search In All Services..."
              onChange={(e) => setServiceInput(e.target.value)}
              IconComponent={() =>
                serviceInput.length ? (
                  <XMarkRegularIcon
                    className="dr-w-24 dr-h-24 dr-p-4 dr-text-darwin-neutral-600 dr-cursor-pointer"
                    onClick={() => {
                      setServiceInput("");
                    }}
                  />
                ) : (
                  <MagnifyingGlassRegularIcon className="dr-w-24 dr-h-24 dr-p-4 dr-text-darwin-neutral-600" />
                )
              }
            />
            <SecondaryButton
              title="Bulk upload"
              IconComponent={() => (
                <UploadRegularIcon className="dr-w-16 dr-h-16" />
              )}
              onClick={() => handleShowUploadModal(true)}
            />
            <PrimaryButton
              title="Create new"
              onClick={handleCreateNewService}
              IconComponent={() => (
                <PlusRegularIcon className="dr-w-16 dr-h-16" />
              )}
            />
          </div>
        </div>
        <div className="dr-flex dr-flex-col dr-w-full dr-border dr-border-darwin-neutral-200 dr-rounded-t-8 dr-overflow-hidden dr-h-[calc(100%-76px)]">
          <TableHeaderUi headerConfig={manageServicesTableHeaderConfig} />
          <div className="dr-flex-1 dr-w-full dr-overflow-y-hidden">
            {isAutosuggestLoading ? (
              <div className="dr-flex dr-justify-center dr-items-center dr-h-full">
                <Spinner className="dr-text-darwin-primary-primary dr-w-24 dr-h-24" />
              </div>
            ) : serviceInput && items.length === 0 && !isAutosuggestLoading ? (
              <div className="dr-w-full dr-h-full dr-flex dr-justify-center dr-items-center dr-flex-col dr-space-y-8">
                <div className="dr-w-108 dr-h-120">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    alt="fs"
                    src="https://cdn.eka.care/vagus/clv0sq3x600ar0tfu6qw2ciuy.webp"
                  />
                </div>
                <div className="SubheadlineRegular dr-text-darwin-neutral-600">
                  Unable to find anything with those parameters, try searching
                  for something else
                </div>
              </div>
            ) : (
              <div
                ref={parentRef}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  contain: "strict",
                }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${items[0]?.start ?? 0}px)`,
                    }}
                  >
                    {items.map((virtualItem) => {
                      const isLastItem =
                        virtualItem.index === serviceRows.length - 1;
                      const currRow = serviceRows[virtualItem.index];
                      const isLoaderRow =
                        virtualItem.index > serviceRows.length - 1;

                      if (isLoaderRow || !currRow) {
                        return (
                          <div className="dr-w-full dr-flex dr-justify-center dr-items-center">
                            <Spinner
                              size={12}
                              className="dr-text-darwin-primary-primary"
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          key={virtualItem.key}
                          data-index={virtualItem.index}
                          ref={rowVirtualizer.measureElement}
                        >
                          <ManageServicesTableRowItem
                            query={debouncedServiceInput}
                            serviceItem={currRow}
                            isLastItem={isLastItem}
                            onServiceEditClick={handleServiceEditClick}
                            clinic_id={clinicId}
                            status={selectedStatus}
                            searchInAllServiceTypes={isSearching}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BulkUploadServiceTemplateModal
        handleShowModal={handleShowUploadModal}
        showModal={showUploadModal}
        handleUploadClick={handleUploadClick}
        isUploadLoding={isUploadLoading}
      />
    </>
  );
};

export default ManageServices;
